import { Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/LocalizedLink";
import { ReactNode } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import SEO from "@/components/SEO";
import { useLocation } from "react-router-dom";
import { getComprehensiveSEOData } from "@/lib/comprehensive-seo-data";
import { RelatedLinks } from "@/components/RelatedLinks";

interface GenericPracticePageProps {
  title: string;
  subtitle: string;
  icon: ReactNode;
  mainContent: ReactNode;
  heroImage?: string;
  category?: "immigration" | "criminal" | "family" | "personalInjury" | "realEstate" | "business" | "probate" | "tax";
}

const GenericPracticePage = ({ title, subtitle, icon, mainContent, heroImage, category }: GenericPracticePageProps) => {
  const { language } = useLanguage();
  const location = useLocation();
  const seoData = getComprehensiveSEOData(location.pathname, language);
  const displayH1 = seoData.h1;
  
  const translations = {
    scheduleConsultation: {
      en: "Schedule Consultation",
      es: "Agende Consulta",
      vi: "Đặt Lịch Tư Vấn"
    },
    call: {
      en: "Call:",
      es: "Llamar:",
      vi: "Gọi:"
    },
    needHelp: {
      en: `Need Help with ${title}?`,
      es: `¿Necesita Ayuda con ${title}?`,
      vi: `Cần Trợ Giúp Về ${title}?`
    },
    contactDesc: {
      en: "Contact us today for experienced legal representation.",
      es: "Contáctenos hoy para representación legal experimentada.",
      vi: "Liên hệ với chúng tôi ngay hôm nay để được đại diện pháp lý có kinh nghiệm."
    }
  };

  return (
    <div className="min-h-screen">
      <SEO />
      <Header />

      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        {heroImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95" />
          </div>
        )}
        {!heroImage && (
          <div className="absolute inset-0 bg-gradient-hero" />
        )}
        <div className="container mx-auto px-4 relative z-10 text-center text-primary-foreground">
          {icon}
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{displayH1}</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-95">
            {subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero">
              <LocalizedLink to="/appointment">{translations.scheduleConsultation[language]}</LocalizedLink>
            </Button>
            <a href="tel:8329303059">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <Phone className="mr-2 h-5 w-5" />
                {translations.call[language]} (832) 930-3059
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {mainContent}
          </div>
        </div>
      </section>

      {category && <RelatedLinks category={category} />}

      <QuickContact />

      <CTASection 
        title={translations.needHelp[language]}
        description={translations.contactDesc[language]}
      />
      <Footer />
    </div>
  );
};

export default GenericPracticePage;
